import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type {
  OnboardingFlowConfig,
  FieldConfig,
  DocumentItemConfig,
} from "./flowConfig.types";

const DEFAULT_PERSONAL_DETAILS_FIELDS: FieldConfig[] = [
  {
    id: "fullName",
    label: "Name",
    placeholder: "Enter Full Name",
    required: true,
    type: "text",
  },
  {
    id: "dateOfBirth",
    label: "Date of Birth",
    required: true,
    type: "date",
  },
  {
    id: "city",
    label: "City",
    placeholder: "Enter City",
    required: true,
    type: "text",
  },
  {
    id: "aadhaarNumber",
    label: "Aadhar Number",
    placeholder: "Enter Your 12 Digit Aadhar Card no",
    required: true,
    type: "numeric",
    validation: { exact_length: 12 },
  },
  {
    id: "panCardNumber",
    label: "Pan Card Number",
    placeholder: "Enter PAN Card no",
    required: true,
    type: "alphanumeric",
    validation: { exact_length: 10 },
  },
  {
    id: "drivingLicenseNumber",
    label: "Driving License Number",
    placeholder: "Enter Driving License no",
    required: false,
    type: "alphanumeric",
  },
  {
    id: "homePhoneNumber",
    label: "Home Phone Number",
    placeholder: "Enter Home Number",
    required: true,
    type: "phone",
  },
  {
    id: "refereeName",
    label: "Referee Name",
    placeholder: "Enter Referee Name",
    required: false,
    type: "text",
  },
  {
    id: "refereeNumber",
    label: "Referee Number",
    placeholder: "Enter Referee Number",
    required: false,
    type: "phone",
  },
];

const DEFAULT_DOCUMENTS: DocumentItemConfig[] = [
  { id: "aadhaarCard", label: "Aadhaar Card", required: true },
  { id: "panCard", label: "Pan Card", required: true },
  { id: "drivingLicense", label: "Driving License", required: false },
];

export const DEFAULT_FLOW_CONFIG: OnboardingFlowConfig = {
  db_types: [
    {
      id: "NORMAL",
      label: "Normal Delivery Boy",
      description: "Standard delivery partner",
      steps: [
        { id: "AadhaarVerification" },
        {
          id: "PersonalDetails",
          config: { fields: DEFAULT_PERSONAL_DETAILS_FIELDS },
        },
        { id: "Documents", config: { documents: DEFAULT_DOCUMENTS } },
        { id: "DocumentOwnership" },
        { id: "YourDocuments", skip_if: "!is_family_kyc" },
        { id: "SignUpSuccessful" },
        { id: "NearestHub", config: { filter_mode: "geo", radius_km: 50 } },
      ],
    },
    {
      id: "LAGGAN",
      label: "Laagan Delivery Boy",
      description: "Laggan delivery partner",
      steps: [
        { id: "AadhaarVerification" },
        {
          id: "PersonalDetails",
          config: { fields: DEFAULT_PERSONAL_DETAILS_FIELDS },
        },
        { id: "Documents", config: { documents: DEFAULT_DOCUMENTS } },
        { id: "DocumentOwnership" },
        { id: "YourDocuments", skip_if: "!is_family_kyc" },
        { id: "AddressDetails" },
        { id: "SignUpSuccessful" },
        { id: "NearestHub", config: { filter_mode: "geo", radius_km: 50 } },
      ],
    },
  ],
};

interface FlowConfigContextValue {
  flowConfig: OnboardingFlowConfig;
  setFlowConfig: (config: OnboardingFlowConfig) => void;
}

const FlowConfigContext = createContext<FlowConfigContextValue>({
  flowConfig: DEFAULT_FLOW_CONFIG,
  setFlowConfig: () => {},
});

export const FlowConfigProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [flowConfig, setFlowConfigState] =
    useState<OnboardingFlowConfig>(DEFAULT_FLOW_CONFIG);

  const setFlowConfig = useCallback((config: OnboardingFlowConfig) => {
    setFlowConfigState(config);
  }, []);

  const value = useMemo(
    () => ({ flowConfig, setFlowConfig }),
    [flowConfig, setFlowConfig],
  );

  return (
    <FlowConfigContext.Provider value={value}>
      {children}
    </FlowConfigContext.Provider>
  );
};

export const useFlowConfig = (): FlowConfigContextValue =>
  useContext(FlowConfigContext);

export default FlowConfigContext;
